import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { useFieldArray, useWatch } from 'react-hook-form'

//UI
import { TabPanel, TabPanels, Tabs } from '@chakra-ui/react'

import { CommercialChecklistForm } from '@/features/offers/components/OfferForm/CommercialChecklistForm'
import CostStructureForm from '@/features/offers/components/OfferForm/CostStructureForm'
import DocumentForm from '@/features/offers/components/OfferForm/DocumentsForm/DocumentForm'
import HandoverForm from '@/features/offers/components/OfferForm/HandoverForm'
import HistoryForm from '@/features/offers/components/OfferForm/HistoryForm/HistoryForm'
import LegalForm from '@/features/offers/components/OfferForm/LegalForm/LegalForm'
import OfferOverviewForm from '@/features/offers/components/OfferForm/OfferOverview/OfferOverview'
import OfferSubSystemTabList from './OfferSubSystemTabList'
import OfferTabList from './OfferTabList'
import OfferSourcingTab from '@/features/offers/offer-detail/tabs/OfferSourcingTab/OfferSourcingTab'
import SubSystemForm from '@/features/offers/components/OfferForm/SubSystemForm'

//Constant
import config from '@/config'
import { ContributorGroupType } from '@/features/contributorGroups/constants'
import { RoleInOffer } from '@/features/offers/constants'
import { offerQuotationGroupPolicyType } from '@/features/offers/constants/offerEditRights/offerQuotationEditRights'

import { selectOfferDetail, selectOfferPermissions } from '@/features/offers/slices/offerDetail.slice'
import { selectPrintCapture } from '@/stores/offers/printOfferModal'
import PRTab from './PRTab/PRTab'
import GCMaterialTab from './OfferGCMaterialTab'
import OfferGCPRTab from './OfferGCPRTab/OfferGCPRTab'
import { OfferBookingStatus } from '@eapproval/offer-shared'
import { isNullish } from '@/utils/helper'
import BookingTab from './BookingTab'

const OfferTabs = ({ tabIndex, setTabIndex }) => {
  // const [searchParams, setSearchParams] = useSearchParams();

  const params = new URLSearchParams(window.location.search)
  //directly go to damex page
  const approveDamex = params.get('approveDamex')
  const costBudgetIndex = params.get('costBudgetIndex')
  const submissionIndex = params.get('submissionIndex')
  const GCPRSubmissionIndex = params.get('GCPRSubmissionIndex')
  const PRCostBudgetIndex = params.get('PRCostBudgetIndex')
  const PRVendorSelectionIndex = params.get('PRVendorSelectionIndex')

  const offerPermissions = useSelector(selectOfferPermissions)

  const { role } = useSelector(selectOfferDetail)

  const status = useWatch({ name: 'status' })
  const contributors = useWatch({ name: 'contributors' })

  const hasErrorTriggered = useRef(false)
  const hasNotification = useRef(false)
  const hasAppliedInitialTabSelection = useRef(false)

  // The print modal drives tab switching during client-side capture, since it renders
  // outside this component and has no direct access to setTabIndex.
  const { targetTabIndex: printCaptureTargetTabIndex } = useSelector(selectPrintCapture)

  useEffect(() => {
    if (printCaptureTargetTabIndex !== null && printCaptureTargetTabIndex !== tabIndex) {
      setTabIndex(printCaptureTargetTabIndex)
    }
  }, [printCaptureTargetTabIndex, tabIndex, setTabIndex])

  const {
    fields: subSystemsFields,
    append: appendSubsystem,
    remove: removeSubSystem,
  } = useFieldArray({
    name: 'subSystems',
  })

  //direct approve redirect and show message

  //Translated
  const canEditSubSystem = offerPermissions[offerQuotationGroupPolicyType.QUOTATION_SUB_SYSTEM]?.edit
  const showLegalTab =
    contributors.find((contributor) => contributor.group === ContributorGroupType.LEGAL) !== undefined &&
    role !== RoleInOffer.PROCUREMENT_OFFICER

  useEffect(() => {
    if (hasAppliedInitialTabSelection.current) {
      return
    }

    //Set Default Tab
    if (!isNullish(approveDamex)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(1)
      return
    }

    if (!isNullish(costBudgetIndex)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(5)
      return
    }

    if (!isNullish(GCPRSubmissionIndex)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(7)
      return
    }

    if (!isNullish(PRCostBudgetIndex) || !isNullish(PRVendorSelectionIndex)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(8)
      return
    }

    if (tabIndex !== 0) {
      hasAppliedInitialTabSelection.current = true
      return
    }

    if (role === RoleInOffer.PROCUREMENT_OFFICER) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(5)
    } else if ([RoleInOffer.GCPR_APPROVER, RoleInOffer.GCPR_APPROVER_DELEGATEE].includes(role)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(7)
    } else if (Object.values(OfferBookingStatus).includes(status)) {
      hasAppliedInitialTabSelection.current = true
      setTabIndex(4)
    } else {
      hasAppliedInitialTabSelection.current = true
    }
  }, [
    role,
    status,
    tabIndex,
    GCPRSubmissionIndex,
    approveDamex,
    costBudgetIndex,
    PRCostBudgetIndex,
    PRVendorSelectionIndex,
    setTabIndex,
  ])

  return (
    <Tabs
      mt={3}
      index={tabIndex}
      isLazy
      colorScheme={'teal'}
      onChange={(index) => {
        setTabIndex(index)
      }}
    >
      <OfferTabList
        subSystems={subSystemsFields}
        role={role}
        tabIndex={tabIndex}
        disableBooking={!status.includes('Booking')}
        disableLegal={!showLegalTab}
      />

      {(tabIndex === 0 || tabIndex > 11) && (
        <OfferSubSystemTabList
          subSystems={subSystemsFields}
          isDisabled={role === RoleInOffer.PROCUREMENT_OFFICER}
          canEditSubSystem={canEditSubSystem}
          addSubSystem={appendSubsystem}
          removeSubSystem={(index) => {
            setTabIndex(0)
            removeSubSystem(index)
          }}
        />
      )}

      <TabPanels id="offerFormTabPanels">
        <TabPanel px="0">
          <OfferOverviewForm />
        </TabPanel>
        <TabPanel>
          <DocumentForm />
        </TabPanel>

        <TabPanel>
          <LegalForm />
        </TabPanel>
        <TabPanel>
          <CommercialChecklistForm />
        </TabPanel>
        <TabPanel>
          <BookingTab />
        </TabPanel>

        <TabPanel>
          <OfferSourcingTab />
        </TabPanel>
        <TabPanel>
          <GCMaterialTab />
        </TabPanel>

        <TabPanel>
          <OfferGCPRTab />
        </TabPanel>

        {config.PREnabled && (
          <TabPanel>
            <PRTab />
          </TabPanel>
        )}

        {config.handoverFormEnabled && (
          <TabPanel>
            <HandoverForm />
          </TabPanel>
        )}

        <TabPanel>
          <CostStructureForm />
        </TabPanel>
        <TabPanel>
          <HistoryForm />
        </TabPanel>
        {subSystemsFields.map((subSystem, index) => (
          <TabPanel key={subSystem.id}>
            <SubSystemForm index={index} />
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  )
}

export default OfferTabs
